import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  Assignment,
  CheckCircle,
  HourglassEmpty,
  Close as CloseIcon,
  LocationCity as LocationCityIcon,
} from '@mui/icons-material';
import { Grid } from '../components/common/TypedGrid';
import adminService from '../services/adminService';
import { getBMCAreasByCity, isCityLevelArea, BMCArea } from '../data/bmcAreas';
import { PageHeader, StatCard } from '../components/common';

interface AreaStats {
  areaId: string;
  areaName: string;
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  totalRevenue: number;
  averageTaskValue: number;
  maxDeliveryRadius: number;
  isActive: boolean;
  demandLevel?: 'high' | 'medium' | 'low';
  pickupAreas?: { [bmcAreaName: string]: number };
  dropAreas?: { [bmcAreaName: string]: number };
}

interface FrequentAreaData {
  areaName: string;
  count: number;
  percentage: number;
  type: 'pickup' | 'drop';
}

const AreaStatistics: React.FC = () => {
  const [areas, setAreas] = useState<any[]>([]);
  const [stats, setStats] = useState<AreaStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bmcDialogOpen, setBmcDialogOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState<'Bhubaneswar' | 'Brahmapur' | 'Cuttack' | null>(null);
  const [bmcAreas, setBmcAreas] = useState<BMCArea[]>([]);
  const [frequentPickupAreas, setFrequentPickupAreas] = useState<FrequentAreaData[]>([]);
  const [frequentDropAreas, setFrequentDropAreas] = useState<FrequentAreaData[]>([]);

  useEffect(() => {
    loadAreaStatistics();
  }, []);

  const loadAreaStatistics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all areas
      const fetchedAreas = await adminService.getAllServiceableAreas();
      setAreas(fetchedAreas);

      // Fetch statistics for each area
      const statsPromises = fetchedAreas.map(async (area) => {
        const areaStats = await adminService.getAreaStatistics(area.id);
        
        // Calculate demand level based on total tasks
        // High: 50+ tasks, Medium: 20-49 tasks, Low: <20 tasks
        let demandLevel: 'high' | 'medium' | 'low' = 'low';
        if (areaStats.totalTasks >= 50) {
          demandLevel = 'high';
        } else if (areaStats.totalTasks >= 20) {
          demandLevel = 'medium';
        }
        
        return {
          areaId: area.id,
          areaName: area.areaName,
          ...areaStats,
          maxDeliveryRadius: area.maxDeliveryRadius,
          isActive: area.isActive,
          demandLevel,
        };
      });

      const allStats = await Promise.all(statsPromises);
      setStats(allStats);
      
      // Calculate frequent pickup and drop areas
      calculateFrequentAreas(allStats);
    } catch (err: any) {
      console.error('Error loading area statistics:', err);
      setError(err.message || 'Failed to load area statistics');
    } finally {
      setLoading(false);
    }
  };

  const calculateFrequentAreas = (areaStats: AreaStats[]) => {
    const pickupFrequencies: { [areaName: string]: number } = {};
    const dropFrequencies: { [areaName: string]: number } = {};
    let totalPickups = 0;
    let totalDrops = 0;
    
    // Aggregate pickup and drop counts from all serviceable areas
    areaStats.forEach(stat => {
      if (stat.pickupAreas) {
        Object.entries(stat.pickupAreas).forEach(([areaName, count]) => {
          pickupFrequencies[areaName] = (pickupFrequencies[areaName] || 0) + count;
          totalPickups += count;
        });
      }
      if (stat.dropAreas) {
        Object.entries(stat.dropAreas).forEach(([areaName, count]) => {
          dropFrequencies[areaName] = (dropFrequencies[areaName] || 0) + count;
          totalDrops += count;
        });
      }
    });
    
    // Convert to sorted arrays (top 10)
    const sortedPickupAreas = Object.entries(pickupFrequencies)
      .map(([areaName, count]) => ({
        areaName,
        count,
        percentage: totalPickups > 0 ? (count / totalPickups) * 100 : 0,
        type: 'pickup' as const
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
    
    const sortedDropAreas = Object.entries(dropFrequencies)
      .map(([areaName, count]) => ({
        areaName,
        count,
        percentage: totalDrops > 0 ? (count / totalDrops) * 100 : 0,
        type: 'drop' as const
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
    
    setFrequentPickupAreas(sortedPickupAreas);
    setFrequentDropAreas(sortedDropAreas);
  };

  const getTotalStats = () => {
    return stats.reduce(
      (acc, curr) => ({
        totalTasks: acc.totalTasks + curr.totalTasks,
        completedTasks: acc.completedTasks + curr.completedTasks,
        pendingTasks: acc.pendingTasks + curr.pendingTasks,
        totalRevenue: acc.totalRevenue + curr.totalRevenue,
      }),
      { totalTasks: 0, completedTasks: 0, pendingTasks: 0, totalRevenue: 0 }
    );
  };

  const handleAreaNameClick = (areaName: string) => {
    if (isCityLevelArea(areaName)) {
      const cityName = areaName as 'Bhubaneswar' | 'Brahmapur' | 'Cuttack';
      setSelectedCity(cityName);
      setBmcAreas(getBMCAreasByCity(cityName));
      setBmcDialogOpen(true);
    }
  };

  const handleCloseBmcDialog = () => {
    setBmcDialogOpen(false);
    setSelectedCity(null);
    setBmcAreas([]);
  };

  const totalStats = getTotalStats();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <PageHeader title="Area Statistics" subtitle="Task volume and revenue by service area" />

      <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard label="Total Tasks" value={totalStats.totalTasks} icon={<Assignment />} tone="primary" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Completed" value={totalStats.completedTasks} icon={<CheckCircle />} tone="success" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Pending" value={totalStats.pendingTasks} icon={<HourglassEmpty />} tone="warning" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Total Revenue"
            value={`₹${totalStats.totalRevenue.toLocaleString('en-IN')}`}
            icon={<AttachMoney />}
            tone="success"
          />
        </Grid>
      </Grid>

      {/* Area-wise Statistics Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Area-wise Performance
          </Typography>
          
          {stats.length === 0 ? (
            <Alert severity="info">
              No area statistics available yet. Create some tasks to see performance data.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell><strong>Area Name</strong></TableCell>
                    <TableCell><strong>Max Radius</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="center"><strong>Demand Level</strong></TableCell>
                    <TableCell align="right"><strong>Total Tasks</strong></TableCell>
                    <TableCell align="right"><strong>Completed</strong></TableCell>
                    <TableCell align="right"><strong>Pending</strong></TableCell>
                    <TableCell align="right"><strong>Total Revenue</strong></TableCell>
                    <TableCell align="right"><strong>Avg Task Value</strong></TableCell>
                    <TableCell align="center"><strong>Performance</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats
                    .sort((a, b) => b.totalTasks - a.totalTasks)
                    .map((areaStat) => {
                      const completionRate = areaStat.totalTasks > 0 
                        ? (areaStat.completedTasks / areaStat.totalTasks) * 100 
                        : 0;
                      
                      const performanceLevel = 
                        completionRate >= 80 ? 'high' :
                        completionRate >= 50 ? 'medium' : 'low';
                      
                      return (
                        <TableRow key={areaStat.areaId} hover>
                          <TableCell>
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 1,
                                cursor: isCityLevelArea(areaStat.areaName) ? 'pointer' : 'default',
                                '&:hover': isCityLevelArea(areaStat.areaName) ? {
                                  color: 'primary.main',
                                  textDecoration: 'underline',
                                } : {}
                              }}
                              onClick={() => handleAreaNameClick(areaStat.areaName)}
                            >
                              {isCityLevelArea(areaStat.areaName) && (
                                <LocationCityIcon fontSize="small" color="primary" />
                              )}
                              <Typography fontWeight={600}>{areaStat.areaName}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{areaStat.maxDeliveryRadius} km</TableCell>
                          <TableCell>
                            <Chip
                              label={areaStat.isActive ? 'Active' : 'Inactive'}
                              color={areaStat.isActive ? 'success' : 'error'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {isCityLevelArea(areaStat.areaName) ? (
                              <Chip 
                                label="N/A" 
                                size="small" 
                                variant="outlined"
                              />
                            ) : (
                              <Chip
                                label={areaStat.demandLevel?.toUpperCase() || 'LOW'}
                                color={
                                  areaStat.demandLevel === 'high' ? 'success' :
                                  areaStat.demandLevel === 'medium' ? 'warning' : 'error'
                                }
                                size="small"
                                variant="filled"
                              />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={600}>{areaStat.totalTasks}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography color="success.main" fontWeight={600}>
                              {areaStat.completedTasks}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography color="warning.main" fontWeight={600}>
                              {areaStat.pendingTasks}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={600} color="success.main">
                              ₹{areaStat.totalRevenue.toLocaleString('en-IN')}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography>
                              ₹{Math.round(areaStat.averageTaskValue).toLocaleString('en-IN')}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {areaStat.totalTasks === 0 ? (
                              <Chip label="No Data" size="small" />
                            ) : (
                              <Chip
                                label={`${Math.round(completionRate)}%`}
                                color={
                                  performanceLevel === 'high' ? 'success' :
                                  performanceLevel === 'medium' ? 'warning' : 'error'
                                }
                                size="small"
                                icon={<TrendingUp />}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Frequent BMC Areas Analytics */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Frequent Pickup Areas */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationCityIcon color="primary" />
                Most Frequent Pickup BMC Areas
              </Typography>
              
              {frequentPickupAreas.length === 0 ? (
                <Alert severity="info">
                  No pickup area data available yet. BMC locality tracking will appear after deliveries are created.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell><strong>Rank</strong></TableCell>
                        <TableCell><strong>BMC Area Name</strong></TableCell>
                        <TableCell align="right"><strong>Pickups</strong></TableCell>
                        <TableCell align="right"><strong>% of Total</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {frequentPickupAreas.map((area, index) => (
                        <TableRow key={area.areaName} hover>
                          <TableCell>
                            <Chip 
                              label={`#${index + 1}`} 
                              size="small" 
                              color={index < 3 ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight={500}>{area.areaName}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={600} color="primary.main">
                              {area.count}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography color="text.secondary">
                              {area.percentage.toFixed(1)}%
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                💡 These are the most common pickup locations at BMC locality level across all deliveries.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Frequent Drop Areas */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationCityIcon color="success" />
                Most Frequent Drop BMC Areas
              </Typography>
              
              {frequentDropAreas.length === 0 ? (
                <Alert severity="info">
                  No drop area data available yet. BMC locality tracking will appear after deliveries are created.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell><strong>Rank</strong></TableCell>
                        <TableCell><strong>BMC Area Name</strong></TableCell>
                        <TableCell align="right"><strong>Drops</strong></TableCell>
                        <TableCell align="right"><strong>% of Total</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {frequentDropAreas.map((area, index) => (
                        <TableRow key={area.areaName} hover>
                          <TableCell>
                            <Chip 
                              label={`#${index + 1}`} 
                              size="small" 
                              color={index < 3 ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight={500}>{area.areaName}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={600} color="success.main">
                              {area.count}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography color="text.secondary">
                              {area.percentage.toFixed(1)}%
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                💡 These are the most common drop-off locations at BMC locality level across all deliveries.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Legends */}
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {/* Demand Level Legend */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
          <Typography variant="caption" color="text.secondary">
            Demand Levels (BMC Areas Only):
          </Typography>
          <Chip label="High (≥50 tasks)" color="success" size="small" />
          <Chip label="Medium (20-49 tasks)" color="warning" size="small" />
          <Chip label="Low (<20 tasks)" color="error" size="small" />
        </Box>
        
        {/* Performance Legend */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
          <Typography variant="caption" color="text.secondary">
            Performance Levels:
          </Typography>
          <Chip label="High (≥80%)" color="success" size="small" />
          <Chip label="Medium (50-79%)" color="warning" size="small" />
          <Chip label="Low (<50%)" color="error" size="small" />
        </Box>
      </Box>

      {/* BMC Areas Dialog */}
      <Dialog 
        open={bmcDialogOpen} 
        onClose={handleCloseBmcDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationCityIcon color="primary" />
              <Typography variant="h6">
                BMC Areas in {selectedCity}
              </Typography>
            </Box>
            <IconButton onClick={handleCloseBmcDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            These are the Municipal Corporation areas/wards within {selectedCity}. 
            Low-demand tracking is implemented at this granular level.
          </Alert>
          
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell><strong>Area Name</strong></TableCell>
                  <TableCell><strong>Ward No.</strong></TableCell>
                  <TableCell><strong>Pincode</strong></TableCell>
                  <TableCell><strong>Coverage Radius</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bmcAreas.map((area) => (
                  <TableRow key={area.id} hover>
                    <TableCell>
                      <Typography fontWeight={500}>{area.name}</Typography>
                    </TableCell>
                    <TableCell>
                      {area.wardNumber ? `Ward ${area.wardNumber}` : '-'}
                    </TableCell>
                    <TableCell>{area.pincode || '-'}</TableCell>
                    <TableCell>
                      {area.coverageRadius ? `~${area.coverageRadius} km` : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            💡 <strong>Tip:</strong> Low-demand warnings are shown only for these BMC-level areas, 
            not for the parent city "{selectedCity}". This provides granular demand insights.
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AreaStatistics;
